<script setup lang="ts">
import { computed, ref } from 'vue'

import { PopoverMenu, PopoverMenuOption } from '@collector/shared-ui'
import {
  hasIncidentMasterAttribute,
  isEmergencyIncident,
} from '@collector/shared-ui-domain'
import { Incident } from '@collector/sportsapi-client'
import { Confirmation } from '@collector/sportsapi-client-legacy'
import { useSwappableIncidents } from '@desktop/views/Relation/composables'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import { IncidentsGroup } from '@desktop/views/Relation/Sports/configuration'

import ChangeIncidentOrderButton from './ChangeIncidentOrder/ChangeIncidentOrderButton.vue'
import DeleteIncidentButton from './DeleteIncidentButton.vue'
import { useHistoryIncidentSettings } from './IncidentDetails/useHistoryIncidentSettings'
import SwapIncidentsButton from './SwapIncidents/SwapIncidentsButton.vue'

interface Props {
  incident: Incident
  displayIncidents: Incident[]
  isDeleted: boolean
  shouldShowChangeOrderButton: boolean
  nextIncident: Incident | IncidentsGroup | undefined
  previousIncident: Incident | IncidentsGroup | undefined
}
const props = defineProps<Props>()

// state
const isSwapModalVisible = ref(false)

// composables
const { swappableIncidentsId } = useSwappableIncidents()
const { swappable } = useHistoryIncidentSettings(props.incident)
const eventIncidentsQueue = useEventIncidentsQueue()

// methods
function deleteIncident(): void {
  for (const incident of props.displayIncidents) {
    eventIncidentsQueue.deleteIncident(incident.id)
  }
}

// computed
const shouldShowSwapButton = computed(
  () =>
    swappable &&
    !props.isDeleted &&
    swappableIncidentsId.value.includes(props.incident.incident_id),
)

// computed
const shouldShowDeleteButton = computed(
  () =>
    !props.isDeleted &&
    !hasIncidentMasterAttribute(props.incident) &&
    !isEmergencyIncident(props.incident) &&
    props.incident.confirmation !== Confirmation.ToBeDone,
)

const popoverMenuOptions = computed(() => {
  const options: PopoverMenuOption[] = []

  if (shouldShowSwapButton.value) {
    options.push({
      name: 'Replace',
      onClick: () => {
        isSwapModalVisible.value = true
      },
    })
  }

  if (shouldShowDeleteButton.value) {
    options.push({ name: 'Delete', onClick: deleteIncident })
  }

  return options
})
</script>

<template>
  <div>
    <div
      v-if="isDeleted"
      class="text-error-5 border-error-6 bg-error-5/10 flex h-6 items-center rounded-sm border px-2.5 text-sm font-bold"
      size="xs"
    >
      <span
        class="@max-[15rem]/history-incident:w-[1ch] @max-[15rem]/history-incident:overflow-hidden"
      >
        REMOVED
      </span>
    </div>
    <template v-else>
      <PopoverMenu
        v-if="popoverMenuOptions.length > 0"
        class="@[15rem]/history-incident:hidden"
        :options="popoverMenuOptions"
      />

      <div
        class="gap-x-1 @max-[15rem]/history-incident:hidden @[16rem]/history-incident:flex"
      >
        <ChangeIncidentOrderButton
          v-if="shouldShowChangeOrderButton"
          :incident
          :nextIncident
          :previousIncident
        />
        <SwapIncidentsButton
          v-if="shouldShowSwapButton"
          v-model="isSwapModalVisible"
          :incident
        />
        <DeleteIncidentButton
          v-if="shouldShowDeleteButton"
          :incident
          data-testid="delete-incident-button"
          @click="deleteIncident"
        />
      </div>
    </template>
  </div>
</template>
